import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { api } from '../../services/api';
import { getTeamLogo } from '../../utils/teamLogos';
import { ChartBar } from '@phosphor-icons/react';

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL = 30000;
const TEAM_COMPARISON_LABELS = {
  投篮: 'boxScore.fg',
  三分: 'boxScore.threePt',
  三分球: 'boxScore.threePt',
  罚球: 'boxScore.ft',
  篮板: 'boxScore.reb',
  前场篮板: 'boxScore.offensiveRebounds',
  后场篮板: 'boxScore.defensiveRebounds',
  进攻篮板: 'boxScore.offensiveRebounds',
  防守篮板: 'boxScore.defensiveRebounds',
  助攻: 'boxScore.ast',
  抢断: 'boxScore.stl',
  盖帽: 'boxScore.blk',
  失误: 'boxScore.to',
  得分: 'boxScore.pts',
  投篮命中率: 'stats.fgPct',
  三分命中率: 'stats.threePct',
  罚球命中率: 'stats.ftPct',
};

const translateTeamComparisonLabel = (label, t) => {
  if (!label) return label;
  if (typeof label !== 'string') return label;
  const normalized = label.trim();
  const key = TEAM_COMPARISON_LABELS[normalized];
  return key ? t(key) : label;
};

/**
 * LiveBoxScore - Displays real-time player and team statistics
 * Enhanced version with quarter scores, player leaders, and detailed stats
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
 */
export function LiveBoxScore({ eventId, market, isLive }) {
  const { t } = useTranslation();
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [liveScoreData, setLiveScoreData] = useState(null);
  const [teamComparison, setTeamComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('comparison'); // 'comparison', 'players', or 'playersHome'

  const fetchBoxScore = useCallback(async () => {
    if (!eventId) return;
    
    try {
      // Fetch both game detail (for box score and periodScores) and live score
      const [detailData, liveData] = await Promise.all([
        api.getGameDetail(eventId, market?.teamA || '', market?.teamB || '').catch(() => null),
        api.getLiveScore(eventId).catch(() => null),
      ]);
      
      if (detailData?.boxScore) {
        setBoxScoreData(detailData.boxScore);
      } else if (detailData?.homeTeam && detailData?.awayTeam) {
        setBoxScoreData(detailData);
      }
      
      // Set team comparison data
      if (detailData?.teamComparison) {
        setTeamComparison(detailData.teamComparison);
      }
      
      // Merge liveData with periodScores from detailData
      if (liveData) {
        // Add periodScores from detailData to liveScoreData
        setLiveScoreData({
          ...liveData,
          periodScores: detailData?.periodScores || [],
        });
      } else if (detailData?.periodScores) {
        // If no liveData but we have periodScores
        setLiveScoreData({
          periodScores: detailData.periodScores,
          period: detailData.status?.period || 0,
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch box score:', err);
      setError(err.message || t('boxScore.dataUnavailable'));
    } finally {
      setLoading(false);
    }
  }, [eventId, market, t]);

  // Initial fetch and polling setup
  useEffect(() => {
    fetchBoxScore();

    if (isLive) {
      const intervalId = setInterval(fetchBoxScore, POLLING_INTERVAL);
      return () => clearInterval(intervalId);
    }
  }, [fetchBoxScore, isLive]);

  // Loading state
  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-48">
          <div className="animate-pulse text-gray-400">{t('common.loading')}</div>
        </div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-gray-400">
          {t('boxScore.dataUnavailable', '技术统计暂时不可用')}
        </div>
      </Card>
    );
  }

  // No data state
  if (!boxScoreData) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <ChartBar size={48} weight="duotone" className="mx-auto mb-3 text-gray-500" />
          <div className="text-gray-400">
            {t('boxScore.noData', '暂无技术统计数据')}
          </div>
          {!isLive && (
            <div className="text-sm text-gray-500 mt-2">
              {t('boxScore.notStartedHint', '比赛开始后将显示技术统计')}
            </div>
          )}
        </div>
      </Card>
    );
  }

  const awayTeam = boxScoreData.awayTeam || { teamName: market?.teamA, players: [], totals: {} };
  const homeTeam = boxScoreData.homeTeam || { teamName: market?.teamB, players: [], totals: {} };

  // Find player leaders - one from each team
  const findLeaders = () => {
    const awayPlayers = (awayTeam.players || []).map(p => ({ ...p, team: 'away', teamName: awayTeam.teamName }));
    const homePlayers = (homeTeam.players || []).map(p => ({ ...p, team: 'home', teamName: homeTeam.teamName }));

    const getLeader = (stat) => {
      // Get top player from each team
      const awayTop = [...awayPlayers].sort((a, b) => (b[stat] || 0) - (a[stat] || 0))[0];
      const homeTop = [...homePlayers].sort((a, b) => (b[stat] || 0) - (a[stat] || 0))[0];
      return [awayTop, homeTop].filter(Boolean);
    };

    return {
      points: getLeader('points'),
      rebounds: getLeader('rebounds'),
      assists: getLeader('assists'),
    };
  };

  const leaders = findLeaders();

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <ChartBar size={24} weight="duotone" className="text-accent" />
        <h3 className="font-semibold text-accent">
          {t('boxScore.title', '技术统计')}
        </h3>
      </div>

      {/* Quarter Scores Table */}
      <QuarterScoresTable 
        awayTeam={awayTeam}
        homeTeam={homeTeam}
        liveScoreData={liveScoreData}
        currentPeriod={liveScoreData?.period || 0}
        market={market}
        t={t}
      />

      {/* Player Leaders */}
      <PlayerLeaders leaders={leaders} market={market} t={t} />

      {/* View toggle buttons - Team Comparison vs Player Stats */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveView('comparison')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'comparison'
              ? 'bg-accent-strong text-white'
              : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
          }`}
        >
          {t('boxScore.teamComparison', '球队对比')}
        </button>
        <button
          onClick={() => setActiveView('players')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'players'
              ? 'bg-accent-strong text-white'
              : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
          }`}
        >
          {awayTeam.teamAbbr || market?.teamA?.substring(0, 3).toUpperCase() || 'AWAY'}
        </button>
        <button
          onClick={() => setActiveView('playersHome')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'playersHome'
              ? 'bg-accent-strong text-white'
              : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
          }`}
        >
          {homeTeam.teamAbbr || market?.teamB?.substring(0, 3).toUpperCase() || 'HOME'}
        </button>
      </div>

      {/* Content based on active view */}
      {activeView === 'comparison' ? (
        <TeamComparisonBars 
          teamComparison={teamComparison} 
          awayTeam={awayTeam}
          homeTeam={homeTeam}
          market={market}
          t={t} 
        />
      ) : (
        <PlayerStatsTable 
          currentTeam={activeView === 'playersHome' ? homeTeam : awayTeam} 
          market={market} 
          activeTeam={activeView === 'playersHome' ? 'home' : 'away'} 
          t={t} 
        />
      )}
    </Card>
  );
}

/**
 * Quarter Scores Table Component
 */
function QuarterScoresTable({ awayTeam, homeTeam, liveScoreData, currentPeriod, market, t }) {
  // Extract periodScores from liveScoreData
  // API returns: periodScores: [{ period: 1, homeScore: 30, awayScore: 30 }, ...]
  const periodScores = liveScoreData?.periodScores || [];
  
  // Also check for linescores format (fallback)
  const homeLinescores = liveScoreData?.linescores?.home || 
                         liveScoreData?.homeLinescores || 
                         liveScoreData?.home?.linescores ||
                         [];
  const awayLinescores = liveScoreData?.linescores?.away || 
                         liveScoreData?.awayLinescores || 
                         liveScoreData?.away?.linescores ||
                         [];
  
  // Determine number of quarters to show (at least 4)
  const numQuarters = Math.max(
    4, 
    currentPeriod || 0,
    homeLinescores.length,
    awayLinescores.length,
    periodScores.length
  );
  
  // Generate quarter headers
  const quarters = [];
  for (let i = 1; i <= numQuarters; i++) {
    if (i <= 4) {
      quarters.push({ label: `${t('boxScore.quarter', '第')}${i}${t('boxScore.quarterSuffix', '节')}`, key: `q${i}` });
    } else {
      quarters.push({ label: `OT${i - 4}`, key: `ot${i - 4}` });
    }
  }

  // Get score for a specific quarter
  const getQuarterScore = (teamType, quarterIndex) => {
    // Try periodScores format first (from game-detail API)
    // periodScores is an array where index corresponds to period - 1
    if (periodScores.length > quarterIndex) {
      const ps = periodScores[quarterIndex];
      if (ps) {
        return teamType === 'away' ? (ps.awayScore ?? '-') : (ps.homeScore ?? '-');
      }
    }
    
    // Try finding by period number
    const psByPeriod = periodScores.find(p => p.period === quarterIndex + 1);
    if (psByPeriod) {
      return teamType === 'away' ? (psByPeriod.awayScore ?? '-') : (psByPeriod.homeScore ?? '-');
    }
    
    // Try linescores array (fallback)
    if (teamType === 'away' && awayLinescores[quarterIndex] !== undefined) {
      return awayLinescores[quarterIndex];
    }
    if (teamType === 'home' && homeLinescores[quarterIndex] !== undefined) {
      return homeLinescores[quarterIndex];
    }
    
    return '-';
  };

  // Calculate total scores
  const awayTotal = liveScoreData?.awayScore || 
                    awayTeam.totals?.points || 
                    periodScores.reduce((sum, p) => sum + (p.awayScore || 0), 0) ||
                    awayLinescores.reduce((sum, s) => sum + (s || 0), 0);
  const homeTotal = liveScoreData?.homeScore || 
                    homeTeam.totals?.points || 
                    periodScores.reduce((sum, p) => sum + (p.homeScore || 0), 0) ||
                    homeLinescores.reduce((sum, s) => sum + (s || 0), 0);

  return (
    <div className="mb-6 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 border-b border-glass-border">
            <th className="text-left py-2 px-2">{t('boxScore.team', '球队')}</th>
            {quarters.map((q, i) => (
              <th key={i} className="text-center py-2 px-2 min-w-[40px]">{q.label}</th>
            ))}
            <th className="text-center py-2 px-2 min-w-[50px] text-positive font-bold">
              {t('boxScore.total', '总分')}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Away team row */}
          <tr className="border-b border-glass-border">
            <td className="py-2 px-2">
              <div className="flex items-center gap-2">
                <img 
                  src={getTeamLogo(awayTeam.teamName || market?.teamA, 24)}
                  alt={awayTeam.teamName}
                  className="w-5 h-5 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="text-gray-300">{awayTeam.teamAbbr || market?.teamA?.substring(0, 3).toUpperCase()}</span>
              </div>
            </td>
            {quarters.map((_, i) => (
              <td key={i} className="text-center py-2 px-2 text-gray-300">
                {getQuarterScore('away', i)}
              </td>
            ))}
            <td className="text-center py-2 px-2 text-positive font-bold">{awayTotal}</td>
          </tr>
          {/* Home team row */}
          <tr className="border-b border-glass-border">
            <td className="py-2 px-2">
              <div className="flex items-center gap-2">
                <img 
                  src={getTeamLogo(homeTeam.teamName || market?.teamB, 24)}
                  alt={homeTeam.teamName}
                  className="w-5 h-5 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="text-gray-300">{homeTeam.teamAbbr || market?.teamB?.substring(0, 3).toUpperCase()}</span>
              </div>
            </td>
            {quarters.map((_, i) => (
              <td key={i} className="text-center py-2 px-2 text-gray-300">
                {getQuarterScore('home', i)}
              </td>
            ))}
            <td className="text-center py-2 px-2 text-positive font-bold">{homeTotal}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * Player Leaders Component - Shows top scorers, rebounders, assisters
 */
function PlayerLeaders({ leaders, market, t }) {
  const categories = [
    { key: 'points', icon: '🏀', label: t('boxScore.scoring', '得分') },
    { key: 'rebounds', icon: '🪣', label: t('boxScore.rebounding', '篮板') },
    { key: 'assists', icon: '🎯', label: t('boxScore.assisting', '助攻') },
  ];

  // Get display name (API returns 'name', not 'playerName')
  const getDisplayName = (player) => {
    const fullName = player?.name || player?.playerName || '';
    // Get last name for display
    return fullName.split(' ').pop() || '-';
  };

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      {categories.map(({ key, icon, label }) => {
        const topTwo = leaders[key] || [];
        if (topTwo.length < 2) return null;

        return (
          <div key={key} className="bg-surface-highlight/50 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
              <span>{icon}</span>
              <span>{label}</span>
            </div>
            <div className="flex items-center justify-between">
              {/* First player */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-surface-highlight/70 flex items-center justify-center overflow-hidden">
                  {topTwo[0]?.headshot ? (
                    <img 
                      src={topTwo[0].headshot} 
                      alt={getDisplayName(topTwo[0])}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-xs text-gray-500">👤</span>
                  )}
                </div>
                <div>
                  <div className="text-lg font-bold text-accent">{topTwo[0]?.[key] || 0}</div>
                  <div className="text-xs text-gray-500 truncate max-w-[60px]">{getDisplayName(topTwo[0])}</div>
                </div>
              </div>
              
              <span className="text-gray-600 text-xs">VS</span>
              
              {/* Second player */}
              <div className="flex items-center gap-2 flex-row-reverse">
                <div className="w-8 h-8 rounded-full bg-surface-highlight/70 flex items-center justify-center overflow-hidden">
                  {topTwo[1]?.headshot ? (
                    <img 
                      src={topTwo[1].headshot} 
                      alt={getDisplayName(topTwo[1])}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-xs text-gray-500">👤</span>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-negative">{topTwo[1]?.[key] || 0}</div>
                  <div className="text-xs text-gray-500 truncate max-w-[60px]">{getDisplayName(topTwo[1])}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Player Stats Table Component - Detailed player statistics
 */
function PlayerStatsTable({ currentTeam, market, activeTeam, t }) {
  // Separate starters and bench
  const starters = (currentTeam.players || []).filter(p => p.starter);
  const bench = (currentTeam.players || []).filter(p => !p.starter);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 border-b border-glass-border">
            <th className="text-left py-2 px-2 sticky left-0 bg-surface-highlight/70">{t('boxScore.player', '球员')}</th>
            <th className="text-center py-2 px-2 min-w-[35px]">{t('boxScore.min', 'MIN')}</th>
            <th className="text-center py-2 px-2 min-w-[35px] text-accent-warm">{t('boxScore.pts', 'PTS')}</th>
            <th className="text-center py-2 px-2 min-w-[35px]">{t('boxScore.reb', 'REB')}</th>
            <th className="text-center py-2 px-2 min-w-[35px]">{t('boxScore.ast', 'AST')}</th>
            <th className="text-center py-2 px-2 min-w-[50px]">{t('boxScore.fg', 'FG')}</th>
            <th className="text-center py-2 px-2 min-w-[50px]">{t('boxScore.threePt', '3PT')}</th>
            <th className="text-center py-2 px-2 min-w-[50px]">{t('boxScore.ft', 'FT')}</th>
            <th className="text-center py-2 px-2 min-w-[35px]">{t('boxScore.stl', 'STL')}</th>
            <th className="text-center py-2 px-2 min-w-[35px]">{t('boxScore.blk', 'BLK')}</th>
            <th className="text-center py-2 px-2 min-w-[35px]">{t('boxScore.to', 'TO')}</th>
            <th className="text-center py-2 px-2 min-w-[40px]">{t('boxScore.plusMinus', '+/-')}</th>
          </tr>
        </thead>
        <tbody>
          {/* Starters section */}
          {starters.length > 0 && (
            <>
              <tr>
                <td colSpan={12} className="py-1 px-2 text-xs text-gray-500 bg-surface-highlight/30">
                  {t('boxScore.starters', '首发')}
                </td>
              </tr>
              {starters.map((player, index) => (
                <PlayerRow key={player.playerId || index} player={player} />
              ))}
            </>
          )}
          
          {/* Bench section */}
          {bench.length > 0 && (
            <>
              <tr>
                <td colSpan={12} className="py-1 px-2 text-xs text-gray-500 bg-surface-highlight/30">
                  {t('boxScore.bench', '替补')}
                </td>
              </tr>
              {bench.map((player, index) => (
                <PlayerRow key={player.playerId || index} player={player} />
              ))}
            </>
          )}
          
          {/* If no starters/bench distinction, show all players */}
          {starters.length === 0 && bench.length === 0 && currentTeam.players?.length > 0 && (
            currentTeam.players.map((player, index) => (
              <PlayerRow key={player.playerId || index} player={player} />
            ))
          )}
          
          {/* No players */}
          {(!currentTeam.players || currentTeam.players.length === 0) && (
            <tr>
              <td colSpan={12} className="text-center py-4 text-gray-500">
                {t('boxScore.noPlayers', '暂无球员数据')}
              </td>
            </tr>
          )}
          
          {/* Team totals row */}
          {currentTeam.totals && (
            <tr className="border-t-2 border-glass-border font-bold text-white bg-surface-highlight/50">
              <td className="py-2 px-2 sticky left-0 bg-surface-highlight/50">
                {t('boxScore.teamTotal', '球队总计')}
              </td>
              <td className="text-center py-2 px-2">-</td>
              <td className="text-center py-2 px-2 text-accent-warm">{currentTeam.totals.points ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.rebounds ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.assists ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.fgMade ?? '-'}-{currentTeam.totals.fgAttempted ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.threeMade ?? '-'}-{currentTeam.totals.threeAttempted ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.ftMade ?? '-'}-{currentTeam.totals.ftAttempted ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.steals ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.blocks ?? '-'}</td>
              <td className="text-center py-2 px-2">{currentTeam.totals.turnovers ?? '-'}</td>
              <td className="text-center py-2 px-2">-</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Team Comparison Bars Component - Shows horizontal comparison bars for team stats
 * Displays stats like FG, 3PT, FT, Rebounds, Assists, etc. with visual bars
 */
function TeamComparisonBars({ teamComparison, awayTeam, homeTeam, market, t }) {
  if (!teamComparison || teamComparison.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        {t('boxScore.noData', '暂无球队对比数据')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Team headers */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <img 
            src={getTeamLogo(awayTeam.teamName || market?.teamA, 24)}
            alt={awayTeam.teamName}
            className="w-6 h-6 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="text-accent-warm font-medium">
            {awayTeam.teamAbbr || market?.teamA?.substring(0, 3).toUpperCase() || 'AWAY'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-accent font-medium">
            {homeTeam.teamAbbr || market?.teamB?.substring(0, 3).toUpperCase() || 'HOME'}
          </span>
          <img 
            src={getTeamLogo(homeTeam.teamName || market?.teamB, 24)}
            alt={homeTeam.teamName}
            className="w-6 h-6 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>

      {/* Comparison bars */}
      {teamComparison.map((stat, index) => {
        const awayValue = stat.awayRaw ?? 0;
        const homeValue = stat.homeRaw ?? 0;
        const maxValue = Math.max(awayValue, homeValue, 0);
        const awayPercent = maxValue > 0 ? (awayValue / maxValue) * 50 : 0;
        const homePercent = maxValue > 0 ? (homeValue / maxValue) * 50 : 0;
        const awayLeads = awayValue > homeValue;
        const homeLeads = homeValue > awayValue;
        const displayLabel = translateTeamComparisonLabel(stat.label, t);

        return (
          <div key={index} className="space-y-1">
            {/* Stat label */}
            <div className="text-center text-xs text-gray-400">{displayLabel}</div>
            
            {/* Values and bars */}
            <div className="flex items-center gap-3">
              {/* Away value */}
              <div className={`w-24 text-right text-sm font-semibold ${awayLeads ? 'text-accent-warm' : 'text-slate-400'}`}>
                {stat.away}
              </div>
              
              {/* Bar container */}
              <div className="relative flex-1 h-4 bg-surface-highlight/60 rounded-full overflow-hidden">
                <div className="absolute inset-y-0 left-1/2 w-px bg-slate-500/50" />
                {/* Away bar (extends left from center) */}
                <div
                  className="absolute right-1/2 h-full bg-accent-warm/90 transition-all duration-300"
                  style={{ width: `${awayPercent}%` }}
                />
                {/* Home bar (extends right from center) */}
                <div
                  className="absolute left-1/2 h-full bg-accent/90 transition-all duration-300"
                  style={{ width: `${homePercent}%` }}
                />
              </div>
              
              {/* Home value */}
              <div className={`w-24 text-left text-sm font-semibold ${homeLeads ? 'text-accent' : 'text-slate-400'}`}>
                {stat.home}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Individual player stats row component
 */
function PlayerRow({ player }) {
  const {
    name,
    playerName,
    position,
    jersey,
    minutes,
    points,
    rebounds,
    assists,
    steals,
    blocks,
    turnovers,
    fgMade,
    fgAttempted,
    threeMade,
    threeAttempted,
    ftMade,
    ftAttempted,
    plusMinus,
    headshot,
  } = player;

  // Use name or playerName (API returns 'name')
  const displayName = name || playerName || '-';

  const formatPlusMinus = (pm) => {
    if (pm == null) return '-';
    const numPm = typeof pm === 'string' ? parseInt(pm, 10) : pm;
    if (numPm > 0) return <span className="text-positive">+{numPm}</span>;
    if (numPm < 0) return <span className="text-negative">{numPm}</span>;
    return <span className="text-gray-400">0</span>;
  };

  return (
    <tr className="border-b border-glass-border hover:bg-surface-highlight/30 transition-colors">
      <td className="py-2 px-2 sticky left-0 bg-surface-highlight/70">
        <div className="flex items-center gap-2">
          {/* Player headshot */}
          <div className="w-8 h-8 rounded-full bg-surface-highlight/70 flex-shrink-0 overflow-hidden">
            {headshot ? (
              <img 
                src={headshot} 
                alt={displayName}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                {jersey || '?'}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <span className="text-white font-medium truncate max-w-[100px]">
              {displayName}
            </span>
            <div className="flex items-center gap-1">
              {jersey && (
                <span className="text-xs text-gray-500">#{jersey}</span>
              )}
              {position && (
                <span className="text-xs text-gray-500">{position}</span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="text-center py-2 px-2 text-gray-400">{minutes ?? '-'}</td>
      <td className="text-center py-2 px-2 text-accent-warm font-semibold">{points ?? '-'}</td>
      <td className="text-center py-2 px-2 text-gray-300">{rebounds ?? '-'}</td>
      <td className="text-center py-2 px-2 text-gray-300">{assists ?? '-'}</td>
      <td className="text-center py-2 px-2 text-gray-300">
        {fgMade != null && fgAttempted != null ? `${fgMade}-${fgAttempted}` : '-'}
      </td>
      <td className="text-center py-2 px-2 text-gray-300">
        {threeMade != null && threeAttempted != null ? `${threeMade}-${threeAttempted}` : '-'}
      </td>
      <td className="text-center py-2 px-2 text-gray-300">
        {ftMade != null && ftAttempted != null ? `${ftMade}-${ftAttempted}` : '-'}
      </td>
      <td className="text-center py-2 px-2 text-gray-300">{steals ?? '-'}</td>
      <td className="text-center py-2 px-2 text-gray-300">{blocks ?? '-'}</td>
      <td className="text-center py-2 px-2 text-gray-300">{turnovers ?? '-'}</td>
      <td className="text-center py-2 px-2">{formatPlusMinus(plusMinus)}</td>
    </tr>
  );
}

export default LiveBoxScore;
